import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../UI/Modal/Modal';
import ConfirmModal from '../../UI/ConfirmModal/ConfirmModal';
import tbl from '../../../assets/Style/Table.module.css';
import cl from './TitlesList.module.css';
import { useTitle } from '../../../hooks/useTitle';

const emptyTitle = {
  id: 0,
  title: '',
  description: '',
  cost_for_dealers: '',
  cost_for_retail: '',
};

const TitlesList = () => {
  const { titleService } = useTitle();
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyTitle);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isEdit = useMemo(() => formData.id !== 0, [formData.id]);

  useEffect(() => {
    titleService.fetch();
  }, []);

  const openCreate = () => {
    setFormData(emptyTitle);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      cost_for_dealers: item.cost_for_dealers ?? '',
      cost_for_retail: item.cost_for_retail ?? '',
    });
    setIsModalOpen(true);
  };

  const openDelete = (item) => {
    setFormData(item);
    setIsDeleteOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    const payload = {
      ...formData,
      cost_for_dealers: parseFloat(formData.cost_for_dealers || 0),
      cost_for_retail: parseFloat(formData.cost_for_retail || 0),
    };

    if (isEdit) {
      await titleService.update(payload);
    } else {
      await titleService.add(payload);
    }

    setIsModalOpen(false);
  };

  const confirmDelete = async () => {
    await titleService.remove(formData.id);
    setIsDeleteOpen(false);
  };

  return (
    <div className={cl.wrapper}>
      <div className={cl.header}>
        <h1>{t('Titles')}</h1>
        <button className={cl.addBtn} onClick={openCreate}>
          + {t('AddTitle')}
        </button>
      </div>

      <div className={tbl.table}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('Title')}</th>
              <th>{t('Description')}</th>
              <th>{t('DealerCost')}</th>
              <th>{t('RetailCost')}</th>
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {titleService.items.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.title}</td>
                <td className={cl.text}>{item.description}</td>
                <td>{item.cost_for_dealers}</td>
                <td>{item.cost_for_retail}</td>
                <td>
                <div className={cl.actions}>
                  <button className={cl.linkBtn} onClick={() => openEdit(item)}>
                    {t('Edit')}
                  </button>
                  <button className={cl.linkBtn} onClick={() => openDelete(item)}>
                    {t('Delete')}
                  </button>
                </div>
                </td>
              </tr>
            ))}
            {titleService.items.length === 0 && (
              <tr>
                <td colSpan={6} className={cl.empty}>
                  {t('NoTitlesYet')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal size="medium" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className={cl.modalHead}>
          <h3>{isEdit ? t('EditTitle') : t('AddTitle')}</h3>
        </div>

        <div className={cl.form}>
          <label className={cl.field}>
            <span>{t('Title')}</span>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              autoComplete="off"
            />
          </label>

          <label className={cl.field}>
            <span>{t('Description')}</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </label>

          <div className={cl.inlineFields}>
            <label className={cl.field}>
              <span>{t('DealerCost')}</span>
              <input
                type="number"
                step="0.01"
                name="cost_for_dealers"
                value={formData.cost_for_dealers}
                onChange={handleChange}
              />
            </label>

            <label className={cl.field}>
              <span>{t('RetailCost')}</span>
              <input
                type="number"
                step="0.01"
                name="cost_for_retail"
                value={formData.cost_for_retail}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>

        <div className={cl.modalActions}>
          <button className={cl.secondaryBtn} onClick={() => setIsModalOpen(false)}>
            {t('Cancel')}
          </button>
          <button className={cl.primaryBtn} onClick={save}>
            {isEdit ? t('Update') : t('Create')}
          </button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t('DeleteTitle')}
        message={t('DeleteTitleConfirm', { title: formData.title })}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
      />
    </div>
  );
};

export default TitlesList;
